/**
 * v4 Today — minimal launcher.
 *
 * Just three things on the screen: the firm logo, three big circle shortcuts,
 * and a quiet "Powered by RoniDoc" footer. All other dashboard data lives one
 * tap deeper.
 */

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Settings as SettingsIcon, Bell, X, MessageSquareText, FileCheck2, Newspaper, ChevronRight,
  Sparkles, Briefcase, FolderOpen, Mic, Play, Pause, ArrowLeft, Reply, Send,
  Inbox as InboxIcon, CalendarClock, MessageCircle, FileText, CheckCircle2, UserCheck,
} from 'lucide-react';
import { useV4 } from './i18n';
import { cn } from '@/lib/utils';

interface InboxMsg {
  id: string;
  type: 'personal' | 'update' | 'news' | 'voice';
  title: string;
  preview: string;
  time: string;
  unread: boolean;
  /** for voice messages only — duration in seconds */
  voiceSec?: number;
}

const INBOX: InboxMsg[] = [
  {
    id: 'm1', type: 'personal', unread: true,
    title: 'Your Q3 BAS is ready to review',
    preview: 'I\'ve prepared your BAS for the March quarter. Total GST $7,820. Have a look and let me know if anything seems off — happy to lodge once you approve.',
    time: '2h ago',
  },
  {
    id: 'm2', type: 'voice', unread: true,
    title: 'Voice note from Marcus',
    preview: 'Quick note about the vehicle deduction — easier to explain than type.',
    time: '4h ago',
    voiceSec: 47,
  },
  {
    id: 'm3', type: 'update', unread: true,
    title: 'Notice of Assessment received',
    preview: 'The ATO confirmed your FY24-25 return. Net refund of $1,140 will land in your account within 5 business days.',
    time: 'Yesterday',
  },
  {
    id: 'm4', type: 'voice', unread: false,
    title: 'Voice note from Marcus',
    preview: 'Re: your question about the new equipment receipt — listen back any time.',
    time: '2 days ago',
    voiceSec: 32,
  },
  {
    id: 'm5', type: 'news', unread: false,
    title: 'EOFY checklist for tradies',
    preview: 'Five things to sort before 30 June: vehicle log, asset write-off receipts, super top-up, prepaid expenses, bad debt review.',
    time: '3 days ago',
  },
  {
    id: 'm6', type: 'news', unread: false,
    title: 'Instant write-off extended to $30k',
    preview: 'The Treasurer announced the small-business instant asset write-off threshold has been bumped from $20k to $30k for FY25-26.',
    time: '1 week ago',
  },
];

export default function HomePageV4() {
  const { t, dir } = useV4();
  const initials = t('firm_initials') || 'UAG';
  const firmName = t('firm_name');
  const [showInbox, setShowInbox] = useState(false);
  const [inbox, setInbox] = useState<InboxMsg[]>(INBOX);
  const unread = inbox.filter(m => m.unread).length;

  const openInbox = () => setShowInbox(true);
  const markAllRead = () => setInbox(p => p.map(m => ({ ...m, unread: false })));
  const readOne = (id: string) => setInbox(p => p.map(m => m.id === id ? { ...m, unread: false } : m));

  return (
    <div className="relative flex min-h-[calc(100vh-80px)] flex-col items-center justify-between gap-12 pt-12 pb-6">
      {/* Messages bell in the top-LEFT corner */}
      <button
        onClick={openInbox}
        className="absolute top-3 left-3 flex h-10 w-10 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground transition-colors rtl:left-auto rtl:right-3"
        aria-label="Messages"
      >
        <Bell className="h-5 w-5" strokeWidth={1.5} />
        {unread > 0 && (
          <span className="absolute -top-1 -right-1 flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-[var(--v4-accent)] px-1 text-[10px] font-bold leading-none text-white ring-2 ring-background">
            {unread}
          </span>
        )}
      </button>

      {/* Quiet settings gear in the top-right corner */}
      <Link
        to="/client-pwa-v4/settings"
        className="absolute top-3 right-3 flex h-9 w-9 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground transition-colors rtl:right-auto rtl:left-3"
        aria-label="Settings"
      >
        <SettingsIcon className="h-5 w-5" strokeWidth={1.5} />
      </Link>

      {/* Firm logo block */}
      <div className="flex flex-col items-center text-center">
        <h1 className="text-[clamp(3.5rem,16vw,5.5rem)] font-light leading-none tracking-tight text-foreground">
          {/* Highlight one letter in the accent color (the LAST one — keeps the
              brand letter dominant) */}
          {[...initials].map((ch, i) => (
            <span
              key={i}
              className={i === Math.floor(initials.length / 2) ? 'text-[var(--v4-accent)]' : ''}
            >
              {ch}
            </span>
          ))}
        </h1>
        <p className="mt-4 text-base font-medium text-muted-foreground tracking-wide">
          {firmName}
        </p>
        <p className="mt-2 text-xs text-muted-foreground/70 italic">
          Your AI accountant in your pocket.
        </p>
      </div>

      {/* Three circle shortcuts — staggered "thinking AI" pulse animation */}
      <div className="flex items-start justify-center gap-4 sm:gap-6">
        <CircleShortcut to="/client-pwa-v4/ask"   icon={Sparkles}   topLabel={t('home_ask_label')}   subLabel={t('home_ask_sub')}  delayMs={0}   />
        <CircleShortcut to="/client-pwa-v4/money" icon={Briefcase}  topLabel={t('home_biz_label')}   subLabel={t('home_biz_sub')}  delayMs={350} />
        <CircleShortcut to="/client-pwa-v4/files" icon={FolderOpen} topLabel={t('home_docs_label')}  subLabel={t('home_docs_sub')} delayMs={700} />
      </div>
      <style>{`
        /* Slow, breath-like pulse: ~2s active swell + ~9s rest = 11s cycle.
           Scale-only so the halo stays perfectly concentric with the button. */
        @keyframes v4-think-pulse {
          0%   { transform: scale(1); }
          9%   { transform: scale(1.06); }
          18%  { transform: scale(1); }
          100% { transform: scale(1); }
        }
        @keyframes v4-think-halo {
          0%   { transform: scale(1);    opacity: 0; }
          6%   { transform: scale(1.18); opacity: 0.35; }
          18%  { transform: scale(1.55); opacity: 0; }
          100% { transform: scale(1);    opacity: 0; }
        }
      `}</style>

      {/* Powered-by footer */}
      <div className="flex items-center justify-center gap-1.5 text-xs text-muted-foreground">
        <span>{t('powered_by_ronidoc')}</span>
        <span className="font-bold text-foreground">
          Roni<span className="text-[var(--v4-accent)]">Doc</span>.ai
        </span>
      </div>

      {/* Messages inbox sheet */}
      {showInbox && (
        <InboxSheet
          messages={inbox}
          onClose={() => setShowInbox(false)}
          onReadOne={readOne}
          onMarkAllRead={markAllRead}
          t={t}
          dir={dir}
        />
      )}
    </div>
  );
}

function InboxSheet({ messages, onClose, onReadOne, onMarkAllRead, t, dir }: {
  messages: InboxMsg[];
  onClose: () => void;
  onReadOne: (id: string) => void;
  onMarkAllRead: () => void;
  t: (k: string) => string;
  dir: 'ltr' | 'rtl';
}) {
  const ICON: Record<InboxMsg['type'], any> = {
    personal: MessageSquareText, update: FileCheck2, news: Newspaper, voice: Mic,
  };
  const typeLabel: Record<InboxMsg['type'], string> = {
    personal: t('type_personal'), update: t('type_update'), news: t('type_news'), voice: t('type_voice'),
  };
  const [playing, setPlaying] = useState<string | null>(null);
  const [progress, setProgress] = useState(0);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [replyText, setReplyText] = useState('');
  const [replied, setReplied] = useState(false);
  const selected = messages.find(m => m.id === selectedId) || null;
  const [tab, setTab] = useState<'messages' | 'timeline'>('messages');

  // Timeline events — activity feed of files, messages, deadlines.
  type TLEvent = { id: string; icon: 'file' | 'message' | 'deadline' | 'done' | 'voice'; title: string; detail: string; time: string };
  const TIMELINE: TLEvent[] = [
    { id: 't1', icon: 'message', title: 'Marcus replied in chat',         detail: '"Yes — it\'s under $20k, you bought it this FY…"', time: '50m ago' },
    { id: 't2', icon: 'voice',   title: 'Voice note from Marcus',         detail: 'Quick note about the vehicle deduction',            time: '4h ago' },
    { id: 't3', icon: 'file',    title: 'New file shared by Marcus',      detail: 'BAS Q3 2025-26 (draft).pdf',                        time: 'Today, 09:14' },
    { id: 't4', icon: 'done',    title: 'You uploaded a receipt',         detail: 'Smith Electrical invoice — $1,240',                 time: 'Yesterday' },
    { id: 't5', icon: 'deadline',title: 'Q3 BAS reminder',                detail: 'Due in 12 days · $7,820',                           time: '2 days ago' },
    { id: 't6', icon: 'message', title: 'You asked AI about expenses',    detail: 'Top spend: Materials (56% of total)',               time: '5 days ago' },
    { id: 't7', icon: 'file',    title: 'New file shared by Marcus',      detail: 'P&L YTD — Apr 2026.pdf',                            time: '1 week ago' },
    { id: 't8', icon: 'file',    title: 'New file shared by Marcus',      detail: 'Balance Sheet — 30 Apr 2026.pdf',                   time: '1 week ago' },
  ];

  // Fake "playback" progress for the voice player UX. In production this
  // would drive a real <audio> element.
  useEffect(() => {
    if (!playing) return;
    const msg = messages.find(m => m.id === playing);
    if (!msg?.voiceSec) return;
    setProgress(0);
    const total = msg.voiceSec * 1000;
    const start = performance.now();
    let raf = 0;
    const tick = (now: number) => {
      const p = Math.min(1, (now - start) / total);
      setProgress(p);
      if (p < 1) raf = requestAnimationFrame(tick);
      else setPlaying(null);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [playing, messages]);
  const hasUnread = messages.some(m => m.unread);

  return (
    <>
      <div onClick={onClose} className="fixed inset-0 z-40 bg-foreground/40 backdrop-blur-sm animate-in fade-in duration-200" />
      <div className="fixed inset-x-0 bottom-0 top-12 z-50 mx-auto flex max-w-[720px] flex-col overflow-hidden rounded-t-2xl border-t border-border bg-background shadow-2xl animate-in slide-in-from-bottom duration-300">
        {/* Header — list view OR detail view */}
        {!selected ? (
          <>
          <div className="flex items-center justify-between border-b border-border px-4 py-3">
            <div className="flex items-center gap-2">
              <InboxIcon className="h-4 w-4 text-[var(--v4-accent-text)]" strokeWidth={1.75} />
              <h2 className="text-sm font-bold">Inbox</h2>
            </div>
            <div className="flex items-center gap-2">
              {tab === 'messages' && hasUnread && (
                <button
                  onClick={onMarkAllRead}
                  className="text-[11px] font-semibold text-[var(--v4-accent-text)] hover:underline"
                >
                  {t('mark_all_read')}
                </button>
              )}
              <button onClick={onClose} className="flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:bg-muted">
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>
          {/* Tabs row — Messages + Timeline. No separate Chat (Ask AI page covers that). */}
          <div className="flex border-b border-border bg-background">
            {([
              { id: 'messages', label: 'Messages', icon: Bell },
              { id: 'timeline', label: 'Timeline', icon: CalendarClock },
            ] as const).map(opt => {
              const I = opt.icon;
              const active = tab === opt.id;
              return (
                <button
                  key={opt.id}
                  onClick={() => setTab(opt.id)}
                  className={cn(
                    'flex-1 flex items-center justify-center gap-1.5 border-b-2 px-2 py-2.5 text-xs font-semibold transition-colors',
                    active ? 'border-[var(--v4-accent)] text-foreground' : 'border-transparent text-muted-foreground hover:text-foreground/80'
                  )}
                >
                  <I className="h-3.5 w-3.5" strokeWidth={1.75} />
                  {opt.label}
                </button>
              );
            })}
          </div>
          </>
        ) : (
          <div className="flex items-center justify-between border-b border-border px-4 py-3">
            <button
              onClick={() => setSelectedId(null)}
              className="flex items-center gap-1.5 text-xs font-semibold text-muted-foreground hover:text-foreground"
            >
              <ArrowLeft className={cn('h-4 w-4', dir === 'rtl' && 'rotate-180')} />
              {t('messages_title')}
            </button>
            <button onClick={onClose} className="flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:bg-muted">
              <X className="h-4 w-4" />
            </button>
          </div>
        )}

        {/* Detail view */}
        {selected && (
          <div className="flex-1 overflow-y-auto">
            <div className="px-5 py-5">
              <div className="flex items-center gap-2 mb-2">
                <div className={cn(
                  'flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-md',
                  selected.type === 'personal' && 'bg-[var(--v4-accent)] text-white',
                  selected.type === 'voice'    && 'bg-[var(--v4-accent)] text-white',
                  selected.type === 'update'   && 'bg-indigo-100 text-indigo-700 dark:bg-indigo-950/40 dark:text-indigo-400',
                  selected.type === 'news'     && 'bg-amber-100 text-amber-700 dark:bg-amber-950/40 dark:text-amber-400',
                )}>
                  {(() => { const I = ICON[selected.type]; return <I className="h-4 w-4" strokeWidth={1.75} />; })()}
                </div>
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">{typeLabel[selected.type]}</p>
                  <p className="text-[10px] text-muted-foreground/70">{selected.time}</p>
                </div>
              </div>
              <h2 className="text-xl font-bold leading-tight text-foreground mt-3">{selected.title}</h2>

              {selected.type === 'voice' && selected.voiceSec ? (
                <div className="mt-4 flex items-center gap-3 rounded-lg border border-border bg-card p-3">
                  <button
                    onClick={() => setPlaying(playing === selected.id ? null : selected.id)}
                    className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-[var(--v4-accent)] text-white hover:bg-[var(--v4-accent-hover)]"
                  >
                    {playing === selected.id ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4 ms-0.5" />}
                  </button>
                  <Waveform progress={playing === selected.id ? progress : 0} />
                  <span className="text-xs font-mono tabular-nums text-muted-foreground">
                    {`${Math.floor(selected.voiceSec/60)}:${String(selected.voiceSec%60).padStart(2,'0')}`}
                  </span>
                </div>
              ) : null}

              <p className="mt-4 text-sm leading-relaxed text-foreground/90">{selected.preview}</p>

              {/* Reply composer */}
              <div className="mt-6 border-t border-border pt-4">
                <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-muted-foreground mb-2">Reply</p>
                {replied ? (
                  <p className="rounded-md border border-[var(--v4-accent)]/40 bg-[var(--v4-accent-tint)] p-3 text-sm text-foreground">
                    {t('message_sent')}
                  </p>
                ) : (
                  <>
                    <textarea
                      value={replyText}
                      onChange={e => setReplyText(e.target.value)}
                      placeholder={t('type_your_message')}
                      rows={3}
                      className="w-full rounded-md border border-border bg-card px-3 py-2 text-sm placeholder:text-muted-foreground/60 focus:border-[var(--v4-accent)] focus:outline-none resize-none"
                    />
                    <div className="mt-2 flex gap-2">
                      <button
                        onClick={() => { setReplied(true); setReplyText(''); }}
                        disabled={!replyText.trim()}
                        className="flex flex-1 items-center justify-center gap-2 rounded-md bg-[var(--v4-accent)] py-2.5 text-sm font-semibold text-white hover:bg-[var(--v4-accent-hover)] disabled:bg-muted disabled:text-muted-foreground"
                      >
                        <Send className="h-4 w-4" />
                        {t('send_now')}
                      </button>
                      <button
                        onClick={() => alert('Voice reply (mock)')}
                        className="flex items-center justify-center gap-2 rounded-md border border-border bg-card px-4 text-sm font-semibold text-foreground hover:bg-muted"
                      >
                        <Mic className="h-4 w-4" />
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Messages tab */}
        {!selected && tab === 'messages' && (
        <div className="flex-1 overflow-y-auto">
          {messages.length === 0 ? (
            <div className="p-12 text-center text-sm text-muted-foreground">{t('messages_empty')}</div>
          ) : (
            <ul className="divide-y divide-border/60">
              {messages.map(m => {
                const Icon = ICON[m.type];
                const isPlaying = playing === m.id;
                const elapsedSec = m.voiceSec ? Math.round(m.voiceSec * (isPlaying ? progress : 0)) : 0;
                const fmtSec = (s: number) => `${Math.floor(s/60)}:${String(s%60).padStart(2,'0')}`;
                return (
                  <li key={m.id}>
                    <button
                      onClick={() => { onReadOne(m.id); setSelectedId(m.id); setReplied(false); setReplyText(''); }}
                      className={cn(
                        'flex w-full items-start gap-3 px-4 py-3 text-start transition-colors hover:bg-muted/40',
                        m.unread && 'bg-[var(--v4-accent-tint)]'
                      )}
                    >
                      <div className={cn(
                        'flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-md',
                        m.type === 'personal' && 'bg-[var(--v4-accent)] text-white',
                        m.type === 'voice'    && 'bg-[var(--v4-accent)] text-white',
                        m.type === 'update'   && 'bg-indigo-100 text-indigo-700 dark:bg-indigo-950/40 dark:text-indigo-400',
                        m.type === 'news'     && 'bg-amber-100 text-amber-700 dark:bg-amber-950/40 dark:text-amber-400',
                      )}>
                        <Icon className="h-4 w-4" strokeWidth={1.75} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-baseline gap-2">
                          <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">{typeLabel[m.type]}</span>
                          <span className="text-[10px] text-muted-foreground/70">·</span>
                          <span className="text-[10px] text-muted-foreground/70">{m.time}</span>
                          {m.unread && <span className="ml-auto h-1.5 w-1.5 flex-shrink-0 rounded-full bg-[var(--v4-accent)]" />}
                        </div>
                        <p className={cn('mt-0.5 text-sm leading-snug', m.unread ? 'font-bold text-foreground' : 'font-semibold text-foreground/90')}>
                          {m.title}
                        </p>

                        {m.type === 'voice' && m.voiceSec ? (
                          <div
                            onClick={e => { e.stopPropagation(); onReadOne(m.id); setPlaying(isPlaying ? null : m.id); }}
                            className="mt-2 flex items-center gap-2.5 rounded-md border border-border bg-card p-2 cursor-pointer hover:border-[var(--v4-accent)]/50 transition-colors"
                          >
                            <span className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-[var(--v4-accent)] text-white">
                              {isPlaying ? <Pause className="h-3.5 w-3.5" /> : <Play className="h-3.5 w-3.5 ms-0.5" />}
                            </span>
                            <Waveform progress={isPlaying ? progress : 0} />
                            <span className="text-[10px] font-mono tabular-nums text-muted-foreground flex-shrink-0">
                              {fmtSec(isPlaying ? elapsedSec : m.voiceSec)}
                            </span>
                          </div>
                        ) : (
                          <p className="mt-1 text-xs leading-relaxed text-muted-foreground line-clamp-2">{m.preview}</p>
                        )}
                      </div>
                      <ChevronRight className={cn('h-4 w-4 flex-shrink-0 text-muted-foreground/40 mt-0.5', dir === 'rtl' && 'rotate-180')} />
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
        )}

        {/* Timeline tab */}
        {!selected && tab === 'timeline' && (
          <div className="flex-1 overflow-y-auto px-4 py-4">
            <div className="relative space-y-3 ps-6">
              <span className="absolute inset-y-2 start-2 w-px bg-border" />
              {TIMELINE.map(ev => {
                const ICON_MAP: Record<TLEvent['icon'], { I: any; cls: string }> = {
                  file:     { I: FileText,       cls: 'bg-[var(--v4-accent-tint)] text-[var(--v4-accent-text)]' },
                  message:  { I: MessageCircle,  cls: 'bg-sky-100 text-sky-700 dark:bg-sky-950/40 dark:text-sky-300' },
                  voice:    { I: Mic,            cls: 'bg-[var(--v4-accent-tint)] text-[var(--v4-accent-text)]' },
                  deadline: { I: CalendarClock,  cls: 'bg-amber-100 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300' },
                  done:     { I: CheckCircle2,   cls: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300' },
                };
                const { I, cls } = ICON_MAP[ev.icon];
                return (
                  <div key={ev.id} className="relative flex gap-3">
                    <div className={cn('absolute -start-[1.55rem] top-0 flex h-7 w-7 items-center justify-center rounded-full ring-4 ring-background', cls)}>
                      <I className="h-3.5 w-3.5" strokeWidth={1.75} />
                    </div>
                    <div className="flex-1 rounded-md border border-border bg-card p-3">
                      <p className="text-sm font-semibold text-foreground leading-tight">{ev.title}</p>
                      <p className="mt-0.5 text-xs text-muted-foreground leading-snug">{ev.detail}</p>
                      <p className="mt-1.5 text-[10px] text-muted-foreground/70">{ev.time}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

      </div>
    </>
  );
}

// 20-bar waveform — bars filled to the left of the playback head.
const WAVE_BARS = [4, 7, 11, 8, 14, 10, 6, 13, 9, 12, 7, 10, 14, 8, 5, 11, 9, 13, 7, 4];
function Waveform({ progress }: { progress: number }) {
  const cutoff = Math.floor(progress * WAVE_BARS.length);
  return (
    <div className="flex flex-1 items-center gap-[2px] h-5">
      {WAVE_BARS.map((h, i) => (
        <span
          key={i}
          className={cn(
            'flex-1 rounded-full transition-colors',
            i < cutoff ? 'bg-[var(--v4-accent)]' : 'bg-muted-foreground/30'
          )}
          style={{ height: `${h * 1.2}px` }}
        />
      ))}
    </div>
  );
}

function CircleShortcut({ to, icon: Icon, topLabel, subLabel, delayMs }: {
  to: string; icon: any; topLabel: string; subLabel: string; delayMs: number;
}) {
  return (
    <div className="relative">
      {/* Halo ripple — same delay so it pulses with the dot */}
      <span
        aria-hidden
        className="pointer-events-none absolute inset-0 rounded-full bg-[var(--v4-accent)]"
        style={{
          animation: 'v4-think-halo 7s ease-in-out infinite',
          animationDelay: `${delayMs}ms`,
        }}
      />
      <Link
        to={to}
        className={cn(
          'relative group flex h-[112px] w-[112px] sm:h-[128px] sm:w-[128px] flex-col items-center justify-center rounded-full',
          'bg-[var(--v4-accent)] text-white shadow-md',
          'hover:bg-[var(--v4-accent-hover)] hover:shadow-lg',
          'active:scale-95 transition-colors'
        )}
        style={{
          animation: 'v4-think-pulse 7s ease-in-out infinite',
          animationDelay: `${delayMs}ms`,
        }}
      >
        <Icon className="h-7 w-7 mb-1 opacity-90" strokeWidth={1.25} />
        <span className="text-xl font-semibold leading-none">{topLabel}</span>
        <span className="mt-1 text-[10px] font-medium uppercase tracking-wider opacity-80">{subLabel}</span>
      </Link>
    </div>
  );
}
